import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, useParams } from "react-router-dom";
import { ProvisioningGuard } from "@/components/auth/ProvisioningGuard";
import { AppAccessGuard } from "@/components/auth/AppAccessGuard";
import { AppInstallGuard } from "@/components/auth/AppInstallGuard";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { MenuPage } from "@/features/menu/MenuPage";
import { ItemListPage } from "@/features/inventory/ItemListPage";
import { ItemListPageV2 } from "@/features/inventory/ItemListPageV2";
import { ItemDetailPage } from "@/features/inventory/item-detail/ItemDetailPage";
import { OrderDetailPage } from "@/features/orders/OrderDetailPage";
import { SalesOrderDetailPage } from "@/features/selling/sales-order-detail/SalesOrderDetailPage";
import { SalesInvoiceListPage } from "@/features/selling/SalesInvoiceListPage";
import { StockEntryListPage } from "@/features/stock/StockEntryListPage";
import { StockReconciliationListPage } from "@/features/stock/StockReconciliationListPage";
import { ItemPriceListPage } from "@/features/inventory/ItemPriceListPage";
import { PricingRuleListPage } from "@/features/inventory/PricingRuleListPage";
import { PricingRuleCreatePage } from "@/features/inventory/pricing-rule/PricingRuleCreatePage";
import { PricingRuleDetailPage } from "@/features/inventory/pricing-rule/PricingRuleDetailPage";
import { ShippingRateRuleCreatePage } from "@/features/inventory/shipping-rate-rule/ShippingRateRuleCreatePage";
import { CustomersListPage } from "@/features/customers/CustomersListPage";
import { CustomerDetailPage } from "@/features/customers/customer-detail/CustomerDetailPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { ReviewSessionsPage } from "@/features/dev/review-sessions";
import { PlaygroundRouter } from "@/features/dev/playground/PlaygroundRouter";
import { StandardListPage } from "@/features/list/StandardListPage";
import { StandardDetailPage } from "@/features/doc/StandardDetailPage";
import { StorefrontPageConfigDetailPage } from "@/features/storefront/StorefrontPageConfigDetailPage";
import { GridaEditorTestPage } from "@/features/storefront/GridaEditorTestPage";
import { ModuleOverviewPage } from "@/features/common/ModuleOverviewPage";
import { AllModulesPage } from "@/features/common/AllModulesPage";
import { PrintFormatsPage } from "@/features/print/PrintFormatsPage";
import { StorefrontSettingsPage } from "@/features/storefront/settings/StorefrontSettingsPage";
import { BrandingPage } from "@/features/storefront/settings/BrandingPage";
import { LineSettingsPage } from "@/features/storefront/settings/LineSettingsPage";
import { CustomerAccountsPage } from "@/features/storefront/settings/CustomerAccountsPage";
import { PricingSettingsPage } from "@/features/storefront/settings/PricingSettingsPage";
import { ProductDisplayPage } from "@/features/storefront/settings/ProductDisplayPage";
import { OrderSettingsPage } from "@/features/storefront/settings/OrderSettingsPage";
import { ShippingReviewsPage } from "@/features/storefront/settings/ShippingReviewsPage";
import { PdpaSettingsPage } from "@/features/storefront/settings/PdpaSettingsPage";
import { AdsTrackingPage } from "@/features/storefront/settings/AdsTrackingPage";
import { SiteSpeedPage } from "@/features/storefront/settings/SiteSpeedPage";
import { LanguagesPage } from "@/features/storefront/settings/LanguagesPage";
import { DomainPage } from "@/features/storefront/domains/DomainPage";
import { PaymentMethodsPage } from "@/features/storefront/payment-methods/PaymentMethodsPage";
import { PaymentSlipsPage } from "@/features/storefront/payment-slips/PaymentSlipsPage";
import { CollectionsPage } from "@/features/storefront/collections";
import { PromotionTemplateListPage } from "@/features/storefront/promotions/PromotionTemplateListPage";
import { PromotionTemplateEditorPage } from "@/features/storefront/promotions/PromotionTemplateEditorPage";
import { PromotionsListPage } from "@/features/promotions/PromotionsListPage";
import { CreatePromotionPage } from "@/features/promotions/CreatePromotionPage";
import { PromotionDetailPage } from "@/features/promotions/PromotionDetailPage";
import { EditPromotionPage } from "@/features/promotions/EditPromotionPage";
import { RewardsPage } from "@/features/rewards/RewardsPage";
import { ReviewsListPage } from "@/features/reviews/ReviewsListPage";
import { LineOAOverviewPage } from "@/features/line-oa/LineOAOverviewPage";
import { LineOARichMenusPage } from "@/features/line-oa/LineOARichMenusPage";
import { LineOARichMenuEditor } from "@/features/line-oa/LineOARichMenuEditor";
import { LineOABroadcastPage } from "@/features/line-oa/LineOABroadcastPage";
import { LineOABroadcastCompose } from "@/features/line-oa/LineOABroadcastCompose";
import { LineOAAudiencePage } from "@/features/line-oa/LineOAAudiencePage";
import { LineCampaignPage } from "@/features/line-oa/LineCampaignPage";
import { LineCampaignSentDetailPage } from "@/features/line-oa/LineCampaignSentDetailPage";
import { SupportPage } from "@/features/support/SupportPage";
import { SupportComposePage } from "@/features/support/SupportComposePage";
import { SupportTicketDetailPage } from "@/features/support/SupportTicketDetailPage";

const OpenCartsPage = lazy(() => import("@/features/dashboard/OpenCartsPage").then(m => ({ default: m.OpenCartsPage })));
const LiveActivityPage = lazy(() => import("@/features/dashboard/LiveActivityPage").then(m => ({ default: m.LiveActivityPage })));
const RealtimeAnalyticsPage = lazy(() => import("@/features/dashboard/RealtimeAnalyticsPage").then(m => ({ default: m.RealtimeAnalyticsPage })));
const SeoPage = lazy(() => import("@/features/seo/SeoPage").then(m => ({ default: m.SeoPage }))); // Plan 1949 — Search Console SEO feature
const SeoDetailPage = lazy(() => import("@/features/seo/SeoDetailPage").then(m => ({ default: m.SeoDetailPage })));
const SocialStatsPage = lazy(() => import("@/features/dashboard/SocialStatsPage").then(m => ({ default: m.SocialStatsPage })));
const MetaAdsPage = lazy(() => import("@/features/marketing/MetaAdsPage").then(m => ({ default: m.MetaAdsPage }))); // Plan 1955
const MetaCampaignDetailPage = lazy(() => import("@/features/marketing/MetaCampaignDetailPage").then(m => ({ default: m.MetaCampaignDetailPage }))); // Plan 1955
const BusinessDashboardPage = lazy(() => import("@/features/business-dashboard/BusinessDashboardPage").then(m => ({ default: m.BusinessDashboardPage })));
const SystemHealthPage = lazy(() => import("@/features/system-health/SystemHealthPage").then(m => ({ default: m.SystemHealthPage }))); // Plan 1934
const BrowseToolsPage = lazy(() => import("@/features/dashboard/BrowseToolsPage"));
const UsagePage = lazy(() => import("@/features/settings/UsagePage"));
const ChannelHubPage = lazy(() => import("@/features/channels/ChannelHubPage"));
const ChannelDetailPage = lazy(() => import("@/features/channels/ChannelDetailPage"));
const ChannelListingsPage = lazy(() => import("@/features/channels/ChannelListingsPage"));
const TikTokCreateProductPage = lazy(() => import("@/features/channels/TikTokCreateProductPage"));
const GoogleBusinessPage = lazy(() => import("@/features/google-business/GoogleBusinessPage"));
const TeamListPage = lazy(() => import("@/features/team/TeamListPage"));
const FulfillmentHomePage = lazy(() => import("@/features/fulfillment/FulfillmentHomePage").then(m => ({ default: m.FulfillmentHomePage })));
const AwaitingShipmentPage = lazy(() => import("@/features/fulfillment/AwaitingShipmentPage").then(m => ({ default: m.AwaitingShipmentPage })));
const FulfillmentDashboardPage = lazy(() => import("@/features/fulfillment/FulfillmentDashboardPage").then(m => ({ default: m.FulfillmentDashboardPage })));
const FulfillOrdersPage = lazy(() => import("@/features/fulfillment/FulfillOrdersPage").then(m => ({ default: m.FulfillOrdersPage })));
const PersonalizationQueuePage = lazy(() => import("@/features/fulfillment/PersonalizationQueuePage").then(m => ({ default: m.PersonalizationQueuePage })));
const FulfillmentSettingsPage = lazy(() => import("@/features/fulfillment/FulfillmentSettingsPage").then(m => ({ default: m.FulfillmentSettingsPage })));
const SessionsPage = lazy(() => import("@/features/fulfillment/SessionsPage").then(m => ({ default: m.SessionsPage })));
const PackAndShipPage = lazy(() => import("@/features/fulfillment/PackAndShipPage").then(m => ({ default: m.PackAndShipPage })));

/** Redirect /item/:name → /inventory/item/:name preserving the dynamic param */
function ItemRedirect() {
  const { name } = useParams<{ name: string }>();
  return <Navigate to={`/inventory/item/${name}`} replace />;
}

/**
 * All routes rendered inside ErpLayout (with persistent sidebar).
 * Extracted from routes/index.tsx for file size management.
 */
export const erpLayoutChildren: RouteObject[] = [
  {
    index: true,
    element: <DashboardPage />,
  },
  // Menu — super-app style full-page navigation (mobile bottom nav)
  {
    path: "menu",
    element: <MenuPage />,
  },
  // Open Carts — follow-up list behind the "ในตะกร้า" KPI
  {
    path: "analytics/carts",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <OpenCartsPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  // Live Activity — full-page event stream with kind filters
  {
    path: "analytics/activity",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <LiveActivityPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  // Social Stats — FB page/post engagement via Chatwoot Meta connections
  {
    path: "analytics/social",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <SocialStatsPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  // Meta Ads — real advertiser-side campaign data (Plan 1955)
  {
    path: "marketing/meta",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <MetaAdsPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  {
    path: "marketing/meta/campaign/:campaignId",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <MetaCampaignDetailPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  // Realtime Analytics — GA-style live visitors view
  {
    path: "analytics/realtime",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <RealtimeAnalyticsPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  // SEO — Google Search Console performance (Plan 1949, standalone feature)
  {
    path: "seo",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <SeoPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  {
    path: "seo/detail",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <SeoDetailPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  // Business Dashboard — configurable analytics dashboard (Plan 1921)
  {
    path: "dashboard/business",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <BusinessDashboardPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  // Business redirect → Dashboard
  {
    path: "business",
    element: <Navigate to="/" replace />,
  },
  // System Health — tenant infra monitoring (Plan 1934)
  {
    path: "system-health",
    element: (
      <ProvisioningGuard>
        <Suspense fallback={null}>
          <SystemHealthPage />
        </Suspense>
      </ProvisioningGuard>
    ),
  },
  // Inventory routes - require provisioning + products access
  {
    path: "item",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <ItemListPageV2 />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Redirect /item/:name to /inventory/item/:name (fix #42 — Coming Soon on short URLs)
  {
    path: "item/:name",
    element: <ItemRedirect />,
  },
  {
    path: "item-page2",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <ItemListPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/item/:name",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <ErrorBoundary>
            <ItemDetailPage />
          </ErrorBoundary>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/item/new",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <ErrorBoundary>
            <ItemDetailPage />
          </ErrorBoundary>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Customization template detail routes - require provisioning + products access
  {
    path: "inventory/customization-templates/new",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Item Customization Template" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/customization-templates/:name",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Item Customization Template" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Redirect /customer → /customers
  {
    path: "customer",
    element: <Navigate to="/customers" replace />,
  },
  // Customer routes - require provisioning + customers access
  {
    path: "customers",
    element: (
      <AppAccessGuard requiredApp="customers">
        <ProvisioningGuard>
          <CustomersListPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "customers/new",
    element: (
      <AppAccessGuard requiredApp="customers">
        <ProvisioningGuard>
          <CustomerDetailPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "customers/:name",
    element: (
      <AppAccessGuard requiredApp="customers">
        <ProvisioningGuard>
          <CustomerDetailPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Order routes - require provisioning + orders access
  {
    path: "orders",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardListPage forcedDoctype="Sales Order" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "orders/new",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <SalesOrderDetailPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "orders/:id",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <SalesOrderDetailPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "orders/:id/legacy",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <OrderDetailPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Selling routes - require provisioning + orders access
  {
    path: "selling/payment-entry",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardListPage forcedDoctype="Payment Entry" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "selling/payment-entry/new",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Payment Entry" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "selling/payment-entry/:name",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Payment Entry" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "selling/sales-invoice",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <SalesInvoiceListPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "selling/sales-invoice/new",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Sales Invoice" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "selling/sales-invoice/:name",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Sales Invoice" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Stock routes - require provisioning + products access
  {
    path: "stock/stock-entry",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StockEntryListPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "stock/stock-entry/new",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Stock Entry" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "stock/stock-entry/:name",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Stock Entry" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "stock/stock-reconciliation",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StockReconciliationListPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "stock/stock-reconciliation/new",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Stock Reconciliation" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "stock/stock-reconciliation/:name",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Stock Reconciliation" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // POS Profile management routes (admin pages stay in ErpLayout)
  {
    path: "pos/profiles",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardListPage forcedDoctype="POS Profile" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "pos/profiles/new",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="POS Profile" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "pos/profiles/:name",
    element: (
      <AppAccessGuard requiredApp="orders">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="POS Profile" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Fulfillment routes - require provisioning + products access
  {
    path: "fulfillment",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <FulfillmentHomePage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "fulfillment/dashboard",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <FulfillmentDashboardPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "fulfillment/awaiting-shipment",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <AwaitingShipmentPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "fulfillment/orders",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <FulfillOrdersPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "fulfillment/sessions",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <SessionsPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "fulfillment/pack-and-ship",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <PackAndShipPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "fulfillment/personalization",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <Suspense fallback={<div />}>
            <PersonalizationQueuePage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "fulfillment/settings",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <FulfillmentSettingsPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Item Price routes - require provisioning + products access
  {
    path: "inventory/item-price",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <ItemPriceListPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/item-price/new",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Item Price" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/item-price/:name",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Item Price" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Pricing Rule routes - require provisioning + products access
  {
    path: "inventory/pricing-rule",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <PricingRuleListPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/pricing-rule/new",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <PricingRuleCreatePage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/pricing-rule/:name",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <PricingRuleDetailPage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Shipping Rate Rule routes
  {
    path: "inventory/shipping-rate-rule",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardListPage forcedDoctype="Shipping Rate Rule" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/shipping-rate-rule/new",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <ShippingRateRuleCreatePage />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "inventory/shipping-rate-rule/:name",
    element: (
      <AppAccessGuard requiredApp="products">
        <ProvisioningGuard>
          <StandardDetailPage forcedDoctype="Shipping Rate Rule" />
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Channel routes - require provisioning + channels access
  {
    path: "channels",
    element: (
      <AppAccessGuard requiredApp="channels">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <ChannelHubPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "channels/:channel",
    element: (
      <AppAccessGuard requiredApp="channels">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <ChannelDetailPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "channels/:channel/listings",
    element: (
      <AppAccessGuard requiredApp="channels">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <ChannelListingsPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  {
    path: "channels/:channel/product/new",
    element: (
      <AppAccessGuard requiredApp="channels">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <TikTokCreateProductPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // Google Business Profile - dedicated page, requires channels access
  {
    path: "google-business",
    element: (
      <AppAccessGuard requiredApp="channels">
        <ProvisioningGuard>
          <Suspense fallback={null}>
            <GoogleBusinessPage />
          </Suspense>
        </ProvisioningGuard>
      </AppAccessGuard>
    ),
  },
  // LINE OA routes
  {
    path: "line-oa",
    element: <LineOAOverviewPage />,
  },
  {
    path: "line-oa/rich-menus",
    element: <LineOARichMenusPage />,
  },
  {
    path: "line-oa/rich-menus/new",
    element: <LineOARichMenuEditor />,
  },
  {
    path: "line-oa/broadcast",
    element: <LineOABroadcastPage />,
  },
  {
    path: "line-oa/broadcast/new",
    element: <LineOABroadcastCompose />,
  },
  {
    path: "line-oa/audience",
    element: <LineOAAudiencePage />,
  },
  {
    path: "line-oa/campaigns",
    element: <LineCampaignPage />,
  },
  {
    path: "line-oa/campaigns/sent/:name",
    element: <LineCampaignSentDetailPage />,
  },
  // Settings - always accessible
  {
    path: "settings/team",
    element: (
      <Suspense fallback={null}>
        <TeamListPage />
      </Suspense>
    ),
  },
  {
    path: "settings/usage",
    element: (
      <Suspense fallback={null}>
        <UsagePage />
      </Suspense>
    ),
  },
  {
    path: "settings",
    element: <SettingsPage />,
  },
  // Browse Tools - accessible even during onboarding
  {
    path: "tools",
    element: (
      <Suspense fallback={null}>
        <BrowseToolsPage />
      </Suspense>
    ),
  },
  // Storefront routes - require install + builder access
  {
    path: "storefront/theme",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><StandardListPage forcedDoctype="Storefront Theme" /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/theme/new",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><StandardDetailPage forcedDoctype="Storefront Theme" /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/theme/:name",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><StandardDetailPage forcedDoctype="Storefront Theme" /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/page-config",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><StandardListPage forcedDoctype="Storefront Page Config" /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/page-config/new",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><StorefrontPageConfigDetailPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/page-config/:name",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><StorefrontPageConfigDetailPage /></AppAccessGuard></AppInstallGuard>,
  },
  // Domain management (dedicated page)
  {
    path: "storefront/domains",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><DomainPage /></AppAccessGuard></AppInstallGuard>,
  },
  // Storefront settings sub-pages
  {
    path: "storefront/settings/branding",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><BrandingPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/line",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><LineSettingsPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/accounts",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><CustomerAccountsPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/pricing",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><PricingSettingsPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/display",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><ProductDisplayPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/orders",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><OrderSettingsPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/shipping",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><ShippingReviewsPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/pdpa",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><PdpaSettingsPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/ads-tracking",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><AdsTrackingPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/languages",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><LanguagesPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/settings/site-speed",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><SiteSpeedPage /></AppAccessGuard></AppInstallGuard>,
  },
  // Storefront settings hub
  {
    path: "storefront/settings",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><StorefrontSettingsPage /></AppAccessGuard></AppInstallGuard>,
  },
  // Mobile design — moved to NON-ERP section (full-bleed, no sidebar)
  // Payment administration
  {
    path: "storefront/payment-methods",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><PaymentMethodsPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/payment-slips",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><PaymentSlipsPage /></AppAccessGuard></AppInstallGuard>,
  },
  // Storefront root → redirect to pages (avoids "Coming Soon" dead end)
  {
    path: "storefront",
    element: <Navigate to="/storefront/collections" replace />,
  },
  {
    path: "storefront/collections",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><CollectionsPage /></AppAccessGuard></AppInstallGuard>,
  },
  // Promotion Tier Templates (Plan 199)
  {
    path: "storefront/promotions",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><PromotionTemplateListPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/promotions/new",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><PromotionTemplateEditorPage /></AppAccessGuard></AppInstallGuard>,
  },
  {
    path: "storefront/promotions/:name",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><PromotionTemplateEditorPage /></AppAccessGuard></AppInstallGuard>,
  },
  // Promotions routes - require install + provisioning + builder access
  {
    path: "promotions",
    element: (
      <AppInstallGuard requiredApp="frappe_builder">
        <AppAccessGuard requiredApp="builder">
          <ProvisioningGuard>
            <PromotionsListPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  {
    path: "promotions/new",
    element: (
      <AppInstallGuard requiredApp="frappe_builder">
        <AppAccessGuard requiredApp="builder">
          <ProvisioningGuard>
            <CreatePromotionPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  {
    path: "promotions/:couponCode/edit",
    element: (
      <AppInstallGuard requiredApp="frappe_builder">
        <AppAccessGuard requiredApp="builder">
          <ProvisioningGuard>
            <EditPromotionPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  {
    path: "promotions/:couponCode",
    element: (
      <AppInstallGuard requiredApp="frappe_builder">
        <AppAccessGuard requiredApp="builder">
          <ProvisioningGuard>
            <PromotionDetailPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  // Reviews — admin review moderation (Plan 100 Epic 04)
  {
    path: "reviews",
    element: (
      <AppInstallGuard requiredApp="frappe_builder">
        <AppAccessGuard requiredApp="builder">
          <ProvisioningGuard>
            <ReviewsListPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  // Rewards — loyalty program setup wizard + management (Plan 177)
  {
    path: "rewards",
    element: (
      <ProvisioningGuard>
        <RewardsPage />
      </ProvisioningGuard>
    ),
  },
  {
    path: "grida-editor-test",
    element: <GridaEditorTestPage />,
  },
  // Print formats — read-only list of custom print formats grouped by DocType
  {
    path: "settings/print-formats",
    element: (
      <ProvisioningGuard>
        <PrintFormatsPage />
      </ProvisioningGuard>
    ),
  },
  // Support — customer help desk (tenant raises tickets to Zaviago).
  // Always accessible; no app-install gate. Backend is mocked in Milestone 1.
  {
    path: "support",
    element: <SupportPage />,
  },
  {
    path: "support/new",
    element: <SupportComposePage />,
  },
  {
    path: "support/:ticketNo",
    element: <SupportTicketDetailPage />,
  },
  // All modules page - shows all available modules in a grid
  {
    path: "modules",
    element: <AllModulesPage />,
  },
  // Module overview page - requires provisioning
  {
    path: "module/:moduleSlug",
    element: (
      <ProvisioningGuard>
        <ModuleOverviewPage />
      </ProvisioningGuard>
    ),
  },
  // Generic list route - requires provisioning
  {
    path: "list/:doctype",
    element: (
      <ProvisioningGuard>
        <StandardListPage />
      </ProvisioningGuard>
    ),
  },
  // Generic detail/create routes - require provisioning
  {
    path: "doc/:doctype/new",
    element: (
      <ProvisioningGuard>
        <StandardDetailPage />
      </ProvisioningGuard>
    ),
  },
  {
    path: "doc/:doctype/name/:name",
    element: (
      <ProvisioningGuard>
        <StandardDetailPage />
      </ProvisioningGuard>
    ),
  },
  {
    path: "doc/:doctype/:name",
    element: (
      <ProvisioningGuard>
        <StandardDetailPage />
      </ProvisioningGuard>
    ),
  },
  // Dev-only routes
  {
    path: "dev/review-sessions",
    element: <ReviewSessionsPage />,
  },
  {
    path: "dev/playground/:slot",
    element: <PlaygroundRouter />,
  },
];
